<template>
    <div
        v-if="organization"
        id="invoice-creator-view"
        class="h-full flex flex-col "
    >
        <div class="h-full w-full flex flex-col">
            <div class="overflow-scroll grow">
                <UCard class="flex flex-col min-h-[calc(100vh-14rem)] max-w-5xl mx-auto w-full grow light" data-theme="light" :ui="{root: 'rounded-none', body: 'flex flex-col gap-2 grow'}">
                    <div class="text-right flex items-center gap-2 justify-end">
                        {{ isCreditNote ? 'KREDITNOTA' : 'FAKTURA' }}
                        <span v-if="nextInvoiceNo" class="font-bold">- {{ formatInvoiceSequence(nextInvoiceNo) }} </span>
                        <invoice-creator-drop-down-menu />
                    </div>
                    <div v-if="nextInvoiceNo?.uninitialized" class="flex flex-col @xl:flex-row pb-4 @xl:justify-between @xl:items-center gap-2">
                        <div class="@xl:w-1/2 flex items-center">
                            <i class="i-ph-warning text-warning inline-block text-[3rem] debug mr-2"/>
                            <div class="border-l-2 border-l-warning pl-2">
                                <div class="text-lg">Startsekvens for fakturanummer</div>
                                <div class="text-sm">Angi første fakturanummer for å starte en ny sekvens. Påfølgende faktura får automatisk neste nummer i denne serien.</div>
                            </div>
                        </div>
                        <UInputNumber
                            v-model="initialInvoiceNo"
                            class="w-full @xl:w-auto"
                            highlight
                            color="warning"
                            placeholder="Fakturanummer"
                            :increment="false"
                            :decrement="false"
                            data-cy="initial-invoice-no-input"
                        />
                    </div>
                    <div class="w-full flex flex-col @xl:flex-row @xl:justify-between gap-4">
                        <div class="@xl:w-1/2 flex flex-col justify-between">
                            <div>
                                <div class="text-muted">Kunde</div>
                                <div class="flex">
                                    <sub-account-picker
                                        v-model:sub-account="customer"
                                        :relation-type="CUSTOMER"
                                        required
                                        autofocus
                                        class="w-full"
                                        theme="light"
                                        data-cy="invoice-creator-account-picker"
                                        @update:model-value="focusDescription"
                                        @created="setCustomer"
                                        @updated="setCustomer"
                                    />
                                    <UButton
                                        v-if="customer"
                                        icon="i-ph-x"
                                        color="error"
                                        variant="ghost"
                                        class="h-fit"
                                        title="Fjern kunde"
                                        @click="clearCustomer"
                                    />
                                </div>
                                <div v-if="customer" class="flex flex-col gap-0 mt-1">
                                    <div class="flex gap-2">
                                        <div>
                                            <div class="flex items-center gap-2">
                                                <div><span class="uppercase">{{ customer.name }}</span> <span v-if="customer.isBusiness" class="text-sm">(Org nr. {{ customer.orgNo }})</span></div>
                                            </div>
                                            <div class="text-sm">{{ customer.address?.addressLine }}, {{ customer.address?.postalCode }} {{ customer?.address?.postalLocation }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <USeparator class="@xl:hidden"/>
                        <div class="flex flex-col @xl:w-1/2">
                            <div class="my-2">{{ organization.businessName }}</div>
                            <div class="text-sm">{{ organization.address?.addressLine }}, {{ organization.address?.postalCode }} {{ organization.address?.postalLocation }}</div>
                            <USeparator/>
                            <div class="flex items-center justify-between gap-2 text-sm">
                                <div class="text-muted">Deres referanse</div>
                                <togglable-edit>
                                    <template #static> {{ invoiceData.customerReference }}</template>
                                    <template #edit="{stopEdit}">
                                        <input
                                            ref="customerRef"
                                            v-model="invoiceData.customerReference"
                                            type="text"
                                            data-cy="invoice-creator-customer-reference"
                                            class="input input-sm rounded-none input-bordered focus:outline-none h-[28px]"
                                            placeholder="Deres referanse"
                                            autocomplete="off"
                                            @blur="stopEdit"
                                        >
                                    </template>
                                </togglable-edit>
                            </div>
                            <div class="flex items-center justify-between gap-2 text-sm">
                                <div class="text-muted">Vår referanse</div>
                                <togglable-edit>
                                    <template #static> {{ invoiceData.supplierReference }}</template>
                                    <template #edit="{stopEdit}">
                                        <input
                                            ref="supplierRef"
                                            v-model="invoiceData.supplierReference"
                                            type="text"
                                            data-cy="invoice-creator-supplier-reference"
                                            class="input input-sm rounded-none input-bordered focus:outline-none h-[28px]"
                                            placeholder="Vår referanse"
                                            autocomplete="off"
                                            @blur="stopEdit"
                                        >
                                    </template>
                                </togglable-edit>
                            </div>
                            <div class="flex items-baseline justify-between gap-2 text-sm">
                                <div class="text-muted">Leveringsdato</div>
                                <togglable-edit>
                                    <template #static>{{ friendlyDate(invoiceData.deliveryDate) }}</template>
                                    <template #edit="{stopEdit}">
                                        <date-picker-v2
                                            v-model="invoiceData.deliveryDate"
                                            default-open
                                            @finished-edit="stopEdit"
                                            @update:model-value="deliveryDateTouched = true"
                                        />
                                    </template>
                                </togglable-edit>
                            </div>
                            <delivery-location-editor />
                            <USeparator/>
                            <div class="flex flex-col gap-0">
                                <div v-if="organization.address" class="flex flex-col text-sm gap-0">
                                    <div class="flex justify-between gap-2">
                                        <div class="flex items-baseline justify-between gap-2">
                                            <div class="text-muted">Fakturadato</div>
                                            <togglable-edit>
                                                <template #static>{{ friendlyDate(invoiceData.invoiceDate) }}</template>
                                                <template #edit="{stopEdit}">
                                                    <date-picker-v2
                                                        v-model="invoiceData.invoiceDate"
                                                        default-open
                                                        @finished-edit="stopEdit"
                                                    />
                                                </template>
                                            </togglable-edit>
                                        </div>
                                        <div v-if="!isCreditNote" class="flex items-baseline justify-between gap-2">
                                            <div class="text-muted">Forfallsdato</div>
                                            <togglable-edit>
                                                <template #static>{{ friendlyDate(invoiceData.dueDate) }}</template>
                                                <template #edit="{stopEdit}">
                                                    <date-picker-v2
                                                        v-model="invoiceData.dueDate"
                                                        default-open
                                                        @finished-edit="stopEdit"
                                                        @update:model-value="dueDateTouched = true"
                                                    />
                                                </template>
                                            </togglable-edit>
                                        </div>
                                    </div>
                                    <togglable-edit :default-editing="!incomeAccount?.bban">
                                        <template #static>
                                            <div class="flex justify-between w-full gap-2">
                                                <div class="whitespace-nowrap text-muted">Betales til</div>
                                                <div v-if="invoiceData.incomeAccountNo && incomeAccount" :title="bankAccount?.name" class="flex items-baseline gap-2">
                                                    <div class="truncate text-right max-w-60">{{ bankAccount?.name }}</div>
                                                    <div class="font-mono text-sm whitespace-nowrap text-right">{{ formatBankAccountNo(incomeAccount?.bban) }}</div>
                                                </div>
                                                <div v-else>Ingen bankkonto angitt</div>
                                            </div>
                                        </template>
                                        <template #edit="{stopEdit}">
                                            <account-picker
                                                v-model:account-no="invoiceData.incomeAccountNo"
                                                :accounts="bankAccounts"
                                                placeholder="Velg bankkonto"
                                                single-pane
                                                show-bank-info
                                                open-left
                                                :filter-by-search="false"
                                                class="h-[28px]"
                                                @on-account-picker-closed="stopEdit"
                                                @update:account-no="stopEdit"
                                            />
                                        </template>
                                    </togglable-edit>
                                </div>
                            </div>
                        </div>
                    </div>
                    <UFormField :ui="{label: 'w-full'}" description="Beskrivelse">
                        <UTextarea
                            ref="descriptionField"
                            v-model="invoiceData.description"
                            class="w-full"
                            :rows="1"
                            autoresize
                        />
                    </UFormField>
                    <div class="@2xl:hidden flex flex-col gap-2">
                        <UCard
                            v-for="(productLine, index) in invoiceData.productLines"
                            :key="productLine.tempUuid"
                            class="shadow"
                            variant="subtle"
                        >
                            <invoice-product-line
                                :product-line="productLine"
                                :index
                                :show-project="showProject"
                                :show-department="showDepartment"
                            />
                        </UCard>
                    </div>
                    <div id="product-lines-grid" class="hidden @2xl:grid">
                        <div class="text-left">Produkt / beskrivelse</div>
                        <div class="text-left">Konto</div>
                        <div>
                            <div v-if="showProject" class="text-left">Prosjekt</div>
                        </div>
                        <div>
                            <div v-if="showDepartment" class="text-left">Avdeling</div>
                        </div>
                        <div class="text-right">Pris</div>
                        <div class="text-right">Ant.</div>
                        <div>
                            <div v-if="showDiscount" class="text-left">Rabatt</div>
                        </div>
                        <div>
                            <div v-if="isVatLiable" class="text-left">MVA</div>
                        </div>
                        <div/>
                        <div class="text-right">Sum</div>

                        <invoice-product-line
                            v-for="(productLine, index) in invoiceData.productLines"
                            :key="productLine.tempUuid"
                            class="hidden @xl:block"
                            :product-line="productLine"
                            :index
                            :show-project="showProject"
                            :show-department="showDepartment"
                        />
                    </div>
                    <div class="flex justify-between items-start gap-2 mt-4">
                        <table class="mt-2 w-72">
                            <tbody v-if="isVatLiable">
                                <tr>
                                    <th v-if="isVatLiable" class="font-normal text-left">MVA-sats</th>
                                    <th class="font-normal text-right">Grunnlag</th>
                                    <th class="font-normal text-right">MVA</th>
                                </tr>
                                <tr v-for="vatSummary in vatSummaries" :key="vatSummary.vatRate">
                                    <td v-if="isVatLiable">{{ formatCurrency(vatSummary.vatRate * 100) }} %</td>
                                    <td class="text-right">{{ formatCurrency(vatSummary.baseAmount) }}</td>
                                    <td class="text-right">{{ formatCurrency(vatSummary.vatAmount) }}</td>
                                </tr>
                            </tbody>
                        </table>
                        <table class="w-1/2 mt-2">
                            <tbody>
                                <tr>
                                    <td>Nettobeløp</td>
                                    <td class="text-right">{{ formatCurrency(productLinesNetSum) }}</td>
                                </tr>
                                <tr v-if="isVatLiable">
                                    <td class="pb-2">
                                        Merverdiavgift
                                    </td>
                                    <td class="text-right pb-2">{{ formatCurrency(productLinesVatAmount) }}</td>
                                </tr>
                                <tr>
                                    <td class="border-t border-t-black pt-2" colspan="2"/>
                                </tr>
                                <tr class="text-lg">
                                    <td>{{ isCreditNote ? 'TIL GODE (NOK)' : 'Å BETALE (NOK)' }}</td>
                                    <td data-cy="product-lines-total-price" class="text-right">{{ formatCurrency(productLinesTotal) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="grow"/>
                    <div class="flex flex-col gap-2">
                        <div class="flex justify-between text-sm items-end">
                            <div class="italic">
                                Ved betaling etter forfall kan det påløpe renter og gebyr etter statens satser.
                                <br>
                                Ved innsigelse vil kravet kunne bli sendt til forliksrådet, jf. tvistlovens § 5-2.
                            </div>
                        </div>
                        <div class="flex gap-4">
                            <div class="font-bold">BETALINGSINFORMASJON</div>
                            <div class="bg-yellow-100 grow"/>
                        </div>
                        <div class="flex">
                            <table class="grow">
                                <tbody>
                                    <tr>
                                        <td>Fakturanummer</td>
                                        <td v-if="nextInvoiceNo">{{ nextInvoiceNo && formatInvoiceSequence(nextInvoiceNo) }}</td>
                                        <td v-else> {{ initialInvoiceNo }}</td>
                                        <td v-if="!isKidEnabled && customer" colspan="2" class="font-bold">Husk å merke betalingen med fakturanummer!</td>
                                        <td v-else/>
                                    </tr>
                                    <tr>
                                        <td>Beløp</td>
                                        <td> {{ formatCurrency(productLinesTotal) }}</td>
                                        <td>Bankkonto</td>
                                        <td>{{ bankAccount && formatBankAccountNo(bankAccount.bban ?? bankAccount.iban) }}</td>
                                        <template v-if="kid">
                                            <td>KID</td>
                                            <td>{{ kid }}</td>
                                        </template>
                                    </tr>
                                </tbody>
                            </table>
                            <div>
                                Laget med <img src="/conta-logo-light.svg" alt="Conta logo">
                            </div>
                        </div>
                        <hr class="mt-2">
                        <div class="flex flex-row gap-2 text-sm uppercase flex-wrap">
                            <span class="font-bold uppercase">{{ selectedOrg?.organizationName }}</span>
                            // <span class="opacity-75">{{ orgNoText }}</span>
                            <span v-if="senderEmail && showEmail" class="opacity-75">// EPOST: {{ senderEmail }}</span>
                            <span v-if="senderPhoneNo && showPhone" class="opacity-75">// TELEFON: {{ senderPhoneNo }}</span>
                        </div>
                    </div>
                </UCard>
            </div>

            <invoice-creator-footer-bar @invoice-created="focusCustomerInput" />
        </div>
    </div>
    <div v-else>Laster foretak...</div>
</template>

<script setup lang="ts">

import {organization} from '@/organization/organization.service'
import {formatInvoiceSequence, useInvoiceCreator} from '@/invoice/invoice.service'
import {accountNoIsValidBankRange, findBankAccountByAccountNo, formatBankAccountNo} from '@/bank/bank-account.service'
import {formatCurrency} from '@/services/formatter.service'
import {orgSelectionComplete, selectedOrg} from '@/tenants/org.service'
import InvoiceProductLine from '@/invoice/InvoiceCreator/InvoiceProductLine.vue'
import AccountPicker from '@/account-picker/AccountPicker.vue'
import {computed, onMounted, onUnmounted, ref} from 'vue'
import {bookkeepingAccounts} from '@/bookkeeping-account/bookkeepingAccount.service'
import {useEmitter} from '@/services/infrastructure/emitter'
import {ORG_SWITCHED} from '@/types/events'
import {FOCUS_INVOICE_REF_FIELD} from '@/types/invoice/InvoiceEvent'
import {friendlyDate} from '@/dateUtils/dateUtils'
import SubAccountPicker from '@/subaccount/components/SubAccountPicker.vue'
import {CUSTOMER, SubAccount} from '@/subaccount/types/SubAccount'
import DatePickerV2 from '@/dateUtils/DatePickerV2.vue'
import InvoiceCreatorDropDownMenu from '@/invoice/InvoiceCreator/InvoiceCreatorDropDownMenu.vue'
import InvoiceCreatorFooterBar from '@/invoice/InvoiceCreator/InvoiceCreatorFooterBar.vue'
import DeliveryLocationEditor from '@/invoice/InvoiceCreator/DeliveryLocationEditor.vue'
import {useSetting} from '@/setting/setting.service'


const { setting: includePhoneSetting } = useSetting<boolean>('INVOICE_INCLUDE_PHONE')
const { setting: includeEmailSetting } = useSetting<boolean>('INVOICE_INCLUDE_EMAIL')
const showPhone = computed(() => {
    return includePhoneSetting.value.value ?? includePhoneSetting.value.default
})
const showEmail = computed(() => {
    return includeEmailSetting.value.value ?? includeEmailSetting.value.default
})

const customerRef = ref(null)
const customerPicker = ref<typeof AccountPicker | null>(null)
type UTextAreaType = {
    textareaRef: HTMLTextAreaElement
}
const descriptionField = ref<UTextAreaType | null>(null)
const orgNoText = computed(() => `${organization.value?.isRegisteredInRegisterOfBusinessEnterprises ? 'Foretaksregisteret ' : ''}${organization.value?.orgNo} ${isVatLiable.value ? 'MVA' : ''}`)

const bankAccounts = computed(() => bookkeepingAccounts.value.filter(a => accountNoIsValidBankRange(a.accountNo)))
const incomeAccount = computed(() => findBankAccountByAccountNo(invoiceData.value.incomeAccountNo))
const {
    customer,
    invoiceData,
    isVatLiable,
    clearCustomer,
    showDiscount,
    showDepartment,
    showProject,
    productLinesNetSum, productLinesVatAmount, productLinesTotal, vatSummaries,
    nextInvoiceNo, initialInvoiceNo,
    isCreditNote,clear,
    initInvoiceCreator, bankAccount, senderPhoneNo, senderEmail, kid, isKidEnabled,
    dueDateTouched, deliveryDateTouched,
} = useInvoiceCreator()

const {cleanUp, onEvent} = useEmitter()

onMounted(async () => {
    await orgSelectionComplete
    await initInvoiceCreator()
})

onEvent(ORG_SWITCHED, async () => {
    clear()
    await initInvoiceCreator()
})

onEvent(FOCUS_INVOICE_REF_FIELD, () => focusRefField())
onUnmounted(cleanUp)

const focusRefField = () => {
    const refField = customerRef.value
    if (!refField) return
    //TODO: Hacky as shit, but doesnt seem to work with nextTick
    setTimeout(() => {
        const inp = refField as HTMLInputElement
        inp.focus()
    }, 100)
}

const focusCustomerInput = () => {
    if (customerPicker.value)
        customerPicker.value.focus()
}

const focusDescription = () => {
    if (descriptionField.value) {
        setTimeout(() => {
            descriptionField.value?.textareaRef.focus()
        }, 100)
    }
}

const setCustomer = (sacc: SubAccount) => {
    customer.value = sacc
    focusDescription()
}

</script>
<style>
@reference "@/index.css";

#product-lines-grid {
    grid-template-columns: minmax(16rem, 1fr) 5rem auto auto minmax(3rem, 7rem) minmax(3rem, 7rem) auto auto 7rem auto;
    column-gap: 0.25rem;
}
</style>